import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { variables } from '@splunk/themes';
import { _ } from '@splunk/ui-utils/i18n';
import moment from '@splunk/moment';
import { DS_STATUS } from '@splunk/datasource-utils';
import { useInterval } from '../hooks';

export interface LastUpdateTimeProps {
    lastUpdated?: string;
    isRealTimeSearch?: boolean;
    status?: string;
}

const Container = styled.div.attrs(() => ({
    'data-test': 'last-update-time',
}))`
    padding-left: 20px;
    padding-right: 10px;
    white-space: nowrap;
    font-size: 12px;
    color: ${variables.contentColorDefault};
`;

const RelativeTime = styled.span`
    font-weight: bold;
`;

const prefix = _('As of');

const getPrefix = ({
    status,
    lastUpdated,
    isRealTimeSearch = false,
}: LastUpdateTimeProps): string => {
    if (isRealTimeSearch) {
        return status === DS_STATUS.PAUSE ? _('Paused') : _('Updating live');
    }

    switch (status) {
        case DS_STATUS.FAILED:
            return _('Failed to update');
        case DS_STATUS.RUNNING:
        case DS_STATUS.FINALIZING:
            return lastUpdated ? prefix : _('Updating');
        default:
            return lastUpdated ? prefix : '';
    }
};

const convertTime = (
    isRealTimeSearch: boolean,
    lastUpdated?: string
): string | null =>
    !isRealTimeSearch && lastUpdated ? moment(lastUpdated).fromNow() : null;

export const LastUpdateTime = ({
    isRealTimeSearch = false,
    lastUpdated,
    status,
}: LastUpdateTimeProps) => {
    // encapsulate relative time calculation
    const updateRelativeTime = useCallback(() => {
        return convertTime(isRealTimeSearch, lastUpdated);
    }, [isRealTimeSearch, lastUpdated]);

    const [relativeTime, setRelativeTime] = useState<string | null>(
        updateRelativeTime
    );
    // reusable helper to update relative time
    const setUpdatedTime = useCallback(() => {
        setRelativeTime(updateRelativeTime());
    }, [updateRelativeTime]);

    // update relative time when lastUpdated or isRealTimeSearch changes (triggered by ref change to updateRelativeTime)
    useEffect(() => {
        setUpdatedTime();
    }, [setUpdatedTime]);

    const timePrefix = getPrefix({ status, lastUpdated, isRealTimeSearch });

    useInterval(setUpdatedTime, status ? 60000 : null);

    if (!status) {
        return null;
    }

    if (!timePrefix && !relativeTime) {
        return null;
    }

    return (
        <Container>
            {timePrefix}
            {timePrefix === prefix ? (
                <RelativeTime> {relativeTime}</RelativeTime>
            ) : null}
        </Container>
    );
};
